import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/apiService';

export function useAudioJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Calculate stats
  const stats = {
    total: jobs.length,
    processing: jobs.filter(j => ['pending', 'extracting', 'translating'].includes(j.status)).length,
    completed: jobs.filter(j => j.status === 'completed').length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAudioJobs();
      setJobs(response);
    } catch (err) {
      setError(err.message || 'Failed to fetch audio jobs');
      console.error('Error fetching audio jobs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create job
  const createJob = useCallback(async (jobData) => {
    try {
      let response;
      
      // Check if this is an uploaded video job
      if (jobData.video_source === 'upload') {
        response = await apiService.createAudioTranslationFromUpload({
          video_url: jobData.video_url,
          video_title: jobData.video_title,
          video_duration: jobData.video_duration,
          source_language: jobData.source_language,
          target_language: jobData.target_language
        });
      } else {
        // YouTube job
        response = await apiService.createAudioTranslation(jobData);
      }
      
      setJobs(prev => [response, ...prev]);
      return response;
    } catch (err) {
      console.error('Error creating audio job:', err);
      throw err;
    }
  }, []);

  // Delete job
  const deleteJob = useCallback(async (jobId) => {
    try {
      await apiService.deleteAudioJob(jobId);
      setJobs(prev => prev.filter(job => job.id !== jobId));
    } catch (err) {
      console.error('Error deleting audio job:', err);
      throw err;
    }
  }, []);

  // Cancel job
  const cancelJob = useCallback(async (jobId) => {
    try {
      await apiService.cancelAudioJob(jobId);
      setJobs(prev => prev.map(job => 
        job.id === jobId 
          ? { ...job, status: 'cancelled', updated_at: new Date().toISOString() }
          : job
      ));
    } catch (err) {
      console.error('Error cancelling audio job:', err);
      throw err;
    }
  }, []);

  // Poll for updates
  const startPolling = useCallback(() => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(async () => {
      // Only poll if there are processing jobs
      const hasProcessingJobs = jobs.some(j => 
        ['pending', 'extracting', 'translating'].includes(j.status)
      );

      if (hasProcessingJobs) {
        try {
          const response = await apiService.getAudioJobs();
          setJobs(response);
        } catch (err) {
          console.error('Error polling audio jobs:', err);
        }
      }
    }, 5000); // Poll every 5 seconds
  }, [jobs]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Effect to manage polling
  useEffect(() => {
    const hasProcessingJobs = jobs.some(j => 
      ['pending', 'extracting', 'translating'].includes(j.status)
    );

    if (hasProcessingJobs) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [jobs, startPolling, stopPolling]);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return {
    jobs,
    loading,
    error,
    stats,
    fetchJobs,
    createJob,
    deleteJob,
    cancelJob
  };
}